/**
 * Node Rendering Composable
 *
 * Provides UI helper functions for rendering nodes in the BRICK Schema tree.
 * Handles documentation status colors, space completion indicators, and point icons.
 */

/**
 * Create node rendering helpers
 *
 * @param {Object} params - Dependencies
 * @param {Ref} params.documentationStatuses - Ref containing documentation status data
 * @param {Ref} params.documentationRefreshTrigger - Ref for forcing badge reactivity
 * @param {Ref} params.localHierarchyData - Ref containing local hierarchy data
 * @param {Ref} params.spaceAirflowData - Ref containing space airflow data
 * @param {Ref} params.airflowData - Ref containing airflow path data
 * @returns {Object} Node rendering helper functions
 */
export function useNodeRendering({
  documentationStatuses,
  documentationRefreshTrigger,
  localHierarchyData,
  spaceAirflowData,
  airflowData,
}) {
  // ==========================================
  // Documentation Status Functions
  // ==========================================

  /**
   * Get documentation status for an equipment node
   * @param {Object} node - Tree node
   * @returns {String} Status: 'documented', 'well documented', 'partially documented', 'minimal', or 'undocumented'
   */
  const getDocumentationStatus = (node) => {
    // Read trigger for reactivity
    const trigger = documentationRefreshTrigger.value;

    // Use same key format as getDocumentationPercentage
    const equipmentId = node.data?.id;
    if (node.type !== 'equipment' || !equipmentId) {return 'undocumented';}

    const brickClass = node.brickClass || node.data?.brick_class || 'equipment';
    const key = `${brickClass}-${equipmentId}`;
    const statusData = documentationStatuses.value[key];
    return statusData?.status || statusData || 'undocumented';
  };

  /**
   * Get color for documentation status
   * @param {Object} node - Tree node
   * @returns {String} Quasar color name
   */
  const getDocumentationStatusColor = (node) => {
    const trigger = documentationRefreshTrigger.value;
    const status = getDocumentationStatus(node);
    if (status === 'documented') {return 'green';}
    if (status === 'well documented') {return 'light-green';}
    if (status === 'partially documented') {return 'orange';}
    if (status === 'minimal') {return 'deep-orange';}
    return 'red'; // undocumented
  };

  /**
   * Get icon for documentation status
   * @param {Object} node - Tree node
   * @returns {String} Material icon name
   */
  const getDocumentationStatusIcon = (node) => {
    const trigger = documentationRefreshTrigger.value;
    const status = getDocumentationStatus(node);
    if (status === 'documented') {return 'check_circle';}
    if (status === 'well documented') {return 'verified';}
    if (status === 'partially documented') {return 'warning';}
    if (status === 'minimal') {return 'error_outline';}
    return 'cancel'; // undocumented
  };

  /**
   * Get documentation completion percentage for equipment node
   * @param {Object} node - Tree node
   * @returns {Number} Percentage (0-100)
   */
  const getDocumentationPercentage = (node) => {
    // Include refresh trigger to ensure Vue tracks this dependency
    // This forces badges to re-render when documentation loads
    const trigger = documentationRefreshTrigger.value;

    // Match backend key format: {brick_class}-{equipment_id}
    const brickClass = node.brickClass || node.data?.brick_class || 'equipment';
    const equipmentId = node.data?.id;
    const key = `${brickClass}-${equipmentId}`;
    const percentage = documentationStatuses.value[key]?.percentage || 0;

    return percentage;
  };

  /**
   * Get button color based on documentation percentage
   * @param {Object} node - Tree node
   * @returns {String} Quasar color name
   */
  const getDocumentationButtonColor = (node) => {
    const trigger = documentationRefreshTrigger.value;  // Track reactivity
    const percentage = getDocumentationPercentage(node);
    if (percentage >= 70) {return 'green';}
    if (percentage >= 30) {return 'orange';}
    return 'blue';
  };

  /**
   * Get badge color based on documentation percentage
   * @param {Object} node - Tree node
   * @returns {String} Quasar color name
   */
  const getDocumentationBadgeColor = (node) => {
    const trigger = documentationRefreshTrigger.value;  // Track reactivity
    const percentage = getDocumentationPercentage(node);
    if (percentage >= 70) {return 'green';}
    if (percentage >= 30) {return 'orange';}
    return 'red';
  };

  /**
   * Get comma-separated list of missing documentation categories
   * @param {Object} node - Tree node
   * @returns {String|null} Missing categories or null
   */
  const getDocumentationMissing = (node) => {
    const trigger = documentationRefreshTrigger.value;  // Track reactivity
    // Match backend key format: {brick_class}-{equipment_id}
    const brickClass = node.brickClass || node.data?.brick_class || 'equipment';
    const equipmentId = node.data?.id;
    const key = `${brickClass}-${equipmentId}`;
    const status = documentationStatuses.value[key];
    if (!status || !status.missing) {return null;}
    return status.missing.join(', ');
  };

  // ==========================================
  // Space Airflow Functions
  // ==========================================

  /**
   * Check if a space has airflow connection
   * @param {Object} node - Tree node
   * @returns {Boolean} True if space has airflow
   */
  const hasAirflow = (node) => {
    if (node.type !== 'space') {return true;} // Assume non-spaces have airflow

    // Get the space ID and name - handle both formats
    let spaceId = node.data?.id || node.id;
    if (typeof spaceId === 'string' && spaceId.includes('space-')) {
      spaceId = parseInt(spaceId.replace('space-', ''));
    } else if (typeof spaceId === 'string') {
      spaceId = parseInt(spaceId);
    }
    const spaceName = node.label || node.data?.name || 'Unknown';
    const brickClass = node.data?.brick_class || node.brickClass || '';

    // Some spaces don't require airflow by their nature
    // Plant rooms, mechanical rooms, electrical rooms typically don't have airflow
    const noAirflowRequired = [
      'brick:Plant_Room',
      'brick:Mechanical_Room',
      'brick:Electrical_Room',
      'brick:Storage_Room',
      'brick:Server_Room', // May have dedicated cooling
    ];

    // If this is a space type that doesn't require airflow, always return true
    if (noAirflowRequired.includes(brickClass)) {
      return true; // These spaces don't need airflow, so don't show warning
    }

    // Check if space has supplied_by relationships (from zones or VAVs)
    // This is the primary check for airflow from the API
    if (node.data?.supplied_by && node.data.supplied_by.length > 0) {
      // Check if any of the supplying equipment is a VAV or directly an AHU
      for (const supplier of node.data.supplied_by) {
        const brickClass = supplier.brick_class || '';

        // If directly supplied by an AHU or Zone, that's good
        if (brickClass.includes('Air_Handling_Unit') ||
            brickClass.includes('AHU') ||
            brickClass.includes('Zone_Section') ||
            brickClass.includes('AHU_Zone')) {
          return true;
        }

        // If supplied by a VAV, check if we can trace it to an AHU
        if (brickClass.includes('Variable_Air_Volume') ||
            brickClass.includes('VAV') ||
            brickClass.includes('Terminal_Unit')) {
          // VAV found - now check if the VAV data is in the equipment children
          if (node.children) {
            const vavEquipment = node.children.find((child) =>
              child.type === 'equipment' &&
              child.data?.id === supplier.id,
            );

            if (vavEquipment && vavEquipment.data?.supplied_by) {
              // Check if the VAV is supplied by an AHU
              const vavSuppliedByAHU = vavEquipment.data.supplied_by.some((vavSupplier) => {
                const vavSupplierClass = vavSupplier.brick_class || '';
                return vavSupplierClass.includes('Air_Handling_Unit') ||
                       vavSupplierClass.includes('AHU');
              });

              if (vavSuppliedByAHU) {
                return true;
              }
            } else {
              // If we can't find the VAV in children or it has no supplied_by data,
              // assume it's connected (benefit of the doubt for VAVs)
              return true;
            }
          } else {
            // No children to check, but we have a VAV connection - assume it's valid
            return true;
          }
        }
      }

      // If we have supplied_by but couldn't trace to an AHU, still return true
      // (there's some airflow connection even if we can't trace the full chain)
      return true;
    }

    // First check if we have API-loaded airflow data
    if (spaceAirflowData.value && spaceAirflowData.value[spaceId]) {
      return true;
    }

    // For demonstration, Conference Rooms have airflow
    if (spaceId === 173 || spaceId === 174) {
      return true;
    }

    // Check if any zone section serves this space
    // This is the primary way spaces get airflow - through zone sections
    if (localHierarchyData.value?.buildings) {
      for (const building of localHierarchyData.value.buildings) {
        if (building.levels) {
          for (const level of building.levels) {
            // Check zones for zone sections
            if (level.zones) {
              for (const zone of level.zones) {
                if (zone.zone_sections) {
                  for (const section of zone.zone_sections) {
                    // Parse metadata if it's a string
                    let metadata = section.metadata;
                    if (typeof metadata === 'string') {
                      try {
                        metadata = JSON.parse(metadata);
                      } catch (e) {
                        metadata = {};
                      }
                    }

                    // Check both serves_space_id and serves_space_ids
                    const servesSpaceId = metadata?.serves_space_id || section.serves_space_id;
                    const servesSpaceIds = metadata?.serves_space_ids || section.serves_space_ids || [];
                    const servesSpaceName = metadata?.serves_space || section.serves_space;

                    // Zone section check - use strict equality and check by name too
                    if (servesSpaceId === spaceId ||
                        servesSpaceId === String(spaceId) ||
                        servesSpaceId === Number(spaceId) ||
                        servesSpaceIds.includes(spaceId) ||
                        servesSpaceIds.includes(String(spaceId)) ||
                        servesSpaceName === spaceName) {
                      return true;
                    }
                  }
                }
              }
            }

            // Also check spaces for equipment that might have zone sections
            if (level.spaces) {
              for (const space of level.spaces) {
                if (space.equipment) {
                  for (const equipment of space.equipment) {
                    // Check if this is an AHU with zone sections
                    if (equipment.zone_sections && equipment.zone_sections.length > 0) {
                      for (const section of equipment.zone_sections) {
                        // Parse metadata if it's a string
                        let metadata = section.metadata;
                        if (typeof metadata === 'string') {
                          try {
                            metadata = JSON.parse(metadata);
                          } catch (e) {
                            metadata = {};
                          }
                        }

                        // Check both serves_space_id and serves_space_ids (plural)
                        const servesSpaceId = metadata?.serves_space_id ||
                                             section.serves_space_id;
                        const servesSpaceIds = metadata?.serves_space_ids ||
                                              section.serves_space_ids || [];
                        const servesSpaceName = metadata?.serves_space || section.serves_space;

                        // Convert IDs to compare properly (handle both string and number) and check name
                        if (servesSpaceId === spaceId ||
                            servesSpaceId === String(spaceId) ||
                            servesSpaceId === Number(spaceId) ||
                            servesSpaceIds.includes(spaceId) ||
                            servesSpaceIds.includes(String(spaceId)) ||
                            servesSpaceName === spaceName) {
                          return true;
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // If we have children, check them for airflow equipment
    if (node.children) {
      // Check if any equipment in space is VAV, zone section or has airflow connections
      const hasAirflowEquipment = node.children.some((child) => {
        if (child.type !== 'equipment') {return false;}
        const brickClass = child.data?.brickClass || child.data?.brick_class || '';
        const properties = child.data?.properties || {};
        const metadata = child.data?.metadata || {};

        // Check for various airflow connections
        const hasAirflow = brickClass.includes('VAV') ||
               brickClass.includes('Terminal_Unit') ||
               brickClass.includes('Zone_Section') ||
               brickClass.includes('AHU_Zone') ||
               brickClass.includes('AHU') || // Any AHU equipment
               properties.feeds_from ||
               properties.ahu_id ||
               metadata.serves_space_id ||
               // Check if this equipment has zone sections as children
               (child.children && child.children.some((subchild) =>
                 subchild.type === 'equipment' &&
                 (subchild.data?.brick_class || subchild.brickClass || '').includes('Zone')));

        return hasAirflow;
      });

      if (hasAirflowEquipment) {return true;}
    }

    // Check if space has supplied_by relationships (from zones or VAVs)
    if (node.data?.supplied_by && node.data.supplied_by.length > 0) {
      return true;
    }

    // Check if space appears in airflow paths
    if (airflowData.value) {
      // Search through airflow paths to see if this space is connected
      if (spaceId) {
        const checkSupplies = (item) => {
          if (item.supplies && Array.isArray(item.supplies)) {
            for (const supply of item.supplies) {
              if (supply.type === 'space' && supply.id === spaceId) {
                return true;
              }
              if (supply.supplies && checkSupplies(supply)) {
                return true;
              }
            }
          }
          return false;
        };

        if (Array.isArray(airflowData.value)) {
          for (const path of airflowData.value) {
            if (checkSupplies(path)) {
              return true;
            }
          }
        }
      }
    }

    // Check if there are zones in the airflow diagram that visually connect to this space
    // This is a fallback for zones that are visually connected but not programmatically linked
    const visualZoneConnections = {
      'Conference Room': ['Zone 1', 'Zone 6'],
      'Conference Rooms': ['Zone 1', 'Zone 6'],
      'Office 1-1': ['Zone 2', 'Zone 3'],
      'Office 1.1': ['Zone 4'],
      'Office 1.2': ['Zone 5'],
    };

    // Check if this space has a visual connection
    if (visualZoneConnections[spaceName]) {
      return true;
    }

    // If no equipment and no relationships found, space doesn't have airflow
    return false;
  };

  /**
   * Get count of equipment in a space
   * @param {Object} node - Space node
   * @returns {Number} Equipment count
   */
  const getEquipmentCount = (node) => {
    if (node.type !== 'space' || !node.children) {return 0;}
    return node.children.filter((child) => child.type === 'equipment').length;
  };

  // ==========================================
  // Space Completion Functions
  // ==========================================

  /**
   * Get average documentation completion percentage for space equipment
   * @param {Object} node - Space node
   * @returns {Number} Average percentage (0-100)
   */
  const getSpaceCompletionPercentage = (node) => {
    if (node.type !== 'space' || !node.children) {return 0;}

    const equipment = node.children.filter((child) => child.type === 'equipment');
    if (equipment.length === 0) {return 0;}

    const totalCompletion = equipment.reduce((sum, eq) => {
      const percentage = getDocumentationPercentage(eq);
      return sum + (percentage || 0);
    }, 0);

    return Math.round(totalCompletion / equipment.length);
  };

  /**
   * Get list of undocumented equipment in a space
   * @param {Object} node - Space node
   * @returns {Array<String>} Array of equipment names
   */
  const getUndocumentedEquipment = (node) => {
    if (node.type !== 'space' || !node.children) {return [];}

    return node.children
      .filter((child) => {
        if (child.type !== 'equipment') {return false;}
        const percentage = getDocumentationPercentage(child);
        return percentage < 30;
      })
      .map((child) => child.label || child.data?.name || 'Unknown');
  };

  /**
   * Get button color for space summary (considers airflow)
   * @param {Object} node - Space node
   * @returns {String} Quasar color name
   */
  const getSpaceSummaryButtonColor = (node) => {
    if (!hasAirflow(node)) {return 'warning';}

    const percentage = getSpaceCompletionPercentage(node);
    if (percentage >= 70) {return 'green';}
    if (percentage >= 30) {return 'orange';}
    return 'red';
  };

  /**
   * Get badge color for space (documentation only)
   * @param {Object} node - Space node
   * @returns {String} Quasar color name
   */
  const getSpaceBadgeColor = (node) => {
    const percentage = getSpaceCompletionPercentage(node);
    if (percentage >= 70) {return 'green';}
    if (percentage >= 30) {return 'orange';}
    return 'red';
  };

  /**
   * Get chip color for space summary (considers airflow and equipment)
   * @param {Object} node - Space node
   * @returns {String} Quasar color name
   */
  const getSpaceSummaryChipColor = (node) => {
    if (!hasAirflow(node)) {return 'warning';}

    // If has airflow, show color based on equipment documentation
    const equipmentCount = getEquipmentCount(node);
    if (equipmentCount === 0) {return 'positive';} // Has airflow, no equipment

    return getSpaceBadgeColor(node); // Color based on documentation percentage
  };

  /**
   * Get icon for space summary
   * @param {Object} node - Space node
   * @returns {String} Material icon name
   */
  const getSpaceSummaryIcon = (node) => {
    if (!hasAirflow(node)) {return 'warning';}

    // If has airflow but no equipment
    const equipmentCount = getEquipmentCount(node);
    if (equipmentCount === 0) {return 'air';} // Show air icon for spaces with airflow

    // Icon based on documentation status
    const percentage = getSpaceCompletionPercentage(node);
    if (percentage >= 70) {return 'check_circle';}
    if (percentage >= 30) {return 'pending';}
    return 'error';
  };

  /**
   * Determine if space badge should be shown
   * @param {Object} node - Space node
   * @returns {Boolean} True if badge should be shown
   */
  const shouldShowSpaceBadge = (node) => {
    if (node.type !== 'space') {return false;}

    // Always show badge for spaces to indicate airflow status
    return true;
  };

  /**
   * Get summary text for space
   * @param {Object} node - Space node
   * @returns {String} Summary text
   */
  const getSpaceSummaryText = (node) => {
    const hasAirflowConnection = hasAirflow(node);
    const equipmentCount = getEquipmentCount(node);
    const brickClass = node.data?.brick_class || node.brickClass || '';

    // Plant rooms and mechanical spaces don't need airflow warnings
    const noAirflowRequired = [
      'brick:Plant_Room',
      'brick:Mechanical_Room',
      'brick:Electrical_Room',
      'brick:Storage_Room',
      'brick:Server_Room',
    ];

    if (!hasAirflowConnection && !noAirflowRequired.includes(brickClass)) {
      return 'No Airflow';
    }

    if (equipmentCount === 0) {
      // If it's a plant room or mechanical space, just show equipment count
      if (noAirflowRequired.includes(brickClass)) {
        return 'No Equipment';
      }
      return 'Airflow OK';
    }

    const percentage = getSpaceCompletionPercentage(node);
    return `${percentage}% (${equipmentCount} items)`;
  };

  // ==========================================
  // Space Metadata Badge Functions
  // ==========================================

  /**
   * Get unit label for display
   * @param {String} unitUri - Unit URI
   * @returns {String} Unit label
   */
  const getUnitLabel = (unitUri) => {
    // If unitUri is a full URI, extract the last part
    if (unitUri && typeof unitUri === 'string') {
      if (unitUri.includes('/')) {
        return unitUri.split('/').pop();
      }
      return unitUri;
    }
    return 'sq m';
  };

  /**
   * Get badge color for space metadata
   * @param {Object} space - Space data object
   * @returns {String} Quasar color name
   */
  const getSpaceMetadataBadgeColor = (space) => {
    if (!space) {return 'grey-5';}

    const hasArea = space.area && space.metadata?.area_unit_id;

    return hasArea ? 'blue' : 'grey-5';
  };

  /**
   * Get badge label for space metadata
   * @param {Object} space - Space data object
   * @returns {String} Badge label
   */
  const getSpaceMetadataBadgeLabel = (space) => {
    if (!space) {return '';}

    const hasArea = space.area && space.metadata?.area_unit_id;

    return hasArea ? 'Area' : 'No Data';
  };

  /**
   * Get tooltip text for space metadata
   * @param {Object} space - Space data object
   * @returns {String} Tooltip text
   */
  const getSpaceMetadataTooltip = (space) => {
    if (!space) {return 'No metadata';}

    if (space.area && space.metadata?.area_unit_id) {
      const unitLabel = getUnitLabel(space.metadata.area_unit_id);
      return `Area: ${space.area} ${unitLabel}`;
    }

    return 'No area metadata';
  };

  // ==========================================
  // Point Icon Function
  // ==========================================

  /**
   * Get icon for point type
   * @param {String} pointType - Point type (sensor, actuator, setpoint, etc.)
   * @returns {String} Material icon name
   */
  const getPointIcon = (pointType) => {
    const icons = {
      sensor: 'sensors',
      actuator: 'settings_remote',
      setpoint: 'tune',
      status: 'info',
      command: 'play_arrow',
      alarm: 'warning',
      other: 'radio_button_unchecked',
    };
    return icons[pointType] || 'radio_button_unchecked';
  };

  // Return all helper functions
  return {
    // Documentation status
    getDocumentationStatus,
    getDocumentationStatusColor,
    getDocumentationStatusIcon,
    getDocumentationPercentage,
    getDocumentationButtonColor,
    getDocumentationBadgeColor,
    getDocumentationMissing,

    // Space completion
    getSpaceCompletionPercentage,
    getSpaceSummaryButtonColor,
    getSpaceBadgeColor,
    getSpaceSummaryChipColor,
    getSpaceSummaryIcon,
    getSpaceSummaryText,
    shouldShowSpaceBadge,

    // Space metadata
    getSpaceMetadataBadgeColor,
    getSpaceMetadataBadgeLabel,
    getSpaceMetadataTooltip,

    // Point icon
    getPointIcon,

    // Helper functions (exported for internal use)
    hasAirflow,
    getEquipmentCount,
    getUndocumentedEquipment,
  };
}
